import { useCallback, useMemo } from 'react';
import Context from 'managers/Context';
import { COMPANIES } from 'constants/Entities';
import CompanyDetailWidget from '../CompaniesDetail/widgets/CompanyDetailWidget';
import GoldenMinuteWidget from 'containers/components/widgets/GoldenMinuteWidget';
import CommentsWidget from 'containers/components/widgets/CommentsWidget';
import EntityLocationWidget from 'containers/components/widgets/EntityLocation';
import { getLiteral } from 'utils/getLiteral';
import { getSmartFieldProps } from 'utils/fm/fields';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { successToast, errorToast } from 'utils/toast';
import useCopilot from 'hooks/useCopilot';
import { getReadOnlyOptions } from 'containers/components/EntityDetail/getReadOnlyOptions';

const useCompaniesDetail = () => {
    const {
        config: {
            permission,
            standardFieldsSchemaMap,
            userData: { lookerAnalyticsViewer = false } = {},
        } = {},
    } = Context.store.getState();

    const crudPermissions = permission?.crud_permission?.[COMPANIES.permission] || null;
    const standardFieldsConfiguration = standardFieldsSchemaMap?.[COMPANIES.extraFieldName] || null;
    const commentFieldReadOnly = standardFieldsConfiguration?.observaciones?.readOnly;

    const { copilotActivated } = useCopilot();

    const { options, optionsParams } = useMemo(() => {
        const options = ['follow'];
        const optionsParams = ['isFollowing'];
        if (permission.merge_companies) options.push('merge');
        options.push('edit');
        if (crudPermissions && crudPermissions.delete) options.push('delete');
        return { options, optionsParams };
    }, [crudPermissions, permission.merge_companies]);

    const getWidgetsProps = useCallback((data) => {
        const activities = {
            addParams: {
                ic: data.id,
            },
            allParams: {
                Company: { Id: data.id, Value: data.name },
            },
        };
        return {
            33: activities,
            1: activities,
            2: {
                accountId: data.id,
                accountName: data.name,
            },
            3: activities,
        };
    }, []);

    const getInitialStateWidgets = useCallback(() => {
        return {
            CompanyDetailWidget: {
                height: 260,
                maxHeight: 1000,
                isEmitted: false,
            },
            ...(copilotActivated && {
                GoldenMinuteWidget: {
                    height: 240,
                    maxHeight: 240,
                },
            }),
            EntityLocationWidget: {
                height: 190,
                maxHeight: 300,
                isEmitted: true,
            },
            CompanyCommentsWidget: {
                height: 176,
                maxHeight: 512,
                isEmitted: false,
            },
            // Family Discounts Widget (ID: 32)
            32: {
                height: 300,
                maxHeight: 512,
                isEmitted: false,
            },
        };
    }, [copilotActivated]);

    const getWidgets = useCallback(
        ({ data, widgets, setSize, loading, configMap, mappedSchema }) => {
            const arr = [
                {
                    id: 'CompanyDetailWidget',
                    widget: (
                        <CompanyDetailWidget
                            type="generalInfo"
                            company={data}
                            setSize={setSize('CompanyDetailWidget')}
                            smartFieldProps={{
                                idCompanyState:
                                    mappedSchema &&
                                    getSmartFieldProps({
                                        field: mappedSchema?.idCompanyState,
                                        configMap,
                                        data,
                                    }),
                            }}
                        />
                    ),
                    x: 0,
                    y: 0,
                    width: 2,
                    height: 296,
                    fillColumn: false,
                },
                copilotActivated && {
                    id: 'GoldenMinuteWidget',
                    widget: (
                        <GoldenMinuteWidget
                            data={data}
                            loading={loading}
                            entity={COMPANIES}
                            id={data.id}
                            setSize={setSize('GoldenMinuteWidget')}
                            tracking={{ summary: 'accountSummaryDana' }}
                            literals={{
                                emptySummary: getLiteral('label_dana_golden_minute_placeholder'),
                                emptySummaryCTA: getLiteral('label_copilot_cta_generate_summary'),
                            }}
                            actionsConfig={{
                                showRefresh: true,
                                showViewSummary: false,
                                customActions: [
                                    {
                                        icon: 'summary',
                                        text: getLiteral('label_copilot_cta_activities_summary'),
                                        onClickPayload: {
                                            prompt: getLiteral(
                                                'label_copilot_cta_activities_summary_internal_prompt',
                                            ),
                                            trackingFunctionality: 'ctaActivitySummary',
                                        },
                                    },
                                    {
                                        icon: 'report',
                                        text: getLiteral('label_copilot_cta_sales_analysis'),
                                        onClickPayload: {
                                            prompt: getLiteral(
                                                'label_copilot_cta_sales_analysis_internal_prompt',
                                            ),
                                            trackingFunctionality: 'ctaSalesGraph',
                                        },
                                    },
                                    {
                                        icon: 'opportunities',
                                        text: getLiteral(
                                            'label_copilot_cta_business_opportunities',
                                        ),
                                        onClickPayload: {
                                            prompt: getLiteral(
                                                'label_copilot_cta_business_opportunities_internal_prompt',
                                            ),
                                            trackingFunctionality: 'ctaOppAnalysis',
                                        },
                                    },
                                ],
                            }}
                        />
                    ),
                    x: 2,
                    y: 0,
                    width: 2,
                    height: 240,
                },
                {
                    id: 'EntityLocationWidget',
                    widget: (
                        <EntityLocationWidget
                            type="location"
                            entityData={data}
                            entity={COMPANIES}
                            allowCheckIn={permission.checkinweb}
                            allowCheckOut={permission.blnAllowCheckOut}
                            notGeoLocalizedEntityLiteral="error_companynotgeolocalized"
                            setSize={setSize('EntityLocationWidget')}
                        />
                    ),
                    x: 2,
                    y: 0,
                    width: 2,
                    height: 190,
                },
                {
                    id: 'CompanyCommentsWidget',
                    widget: (
                        <CommentsWidget
                            title={getLiteral('label_comment_companies')}
                            data={data}
                            comments={data?.comments}
                            setSize={setSize('CompanyCommentsWidget')}
                            entity={COMPANIES}
                            field="comments"
                            isReadOnly={
                                (mappedSchema &&
                                    getSmartFieldProps({
                                        field: mappedSchema?.comments,
                                        configMap,
                                        data,
                                    })?.readOnly) ||
                                !crudPermissions?.update ||
                                getBackendBoolean(data?.isReadOnly) ||
                                commentFieldReadOnly
                            }
                        />
                    ),
                    x: 2,
                    y: 0,
                    width: 2,
                    height: 176,
                },
                ...widgets,
            ].filter(Boolean);

            return arr;
        },
        [
            copilotActivated,
            permission.checkinweb,
            permission.blnAllowCheckOut,
            crudPermissions?.update,
            commentFieldReadOnly,
        ],
    );

    const getWidgetsPermissions = useCallback(
        (data, permissions) => {
            return {
                CompanyCommentsWidget: !standardFieldsConfiguration?.observaciones?.isHidden,
                9: (permissions?.documentManagement && permissions?.documentos_empresas) || false,
                28: (permissions?.documentManagement && permissions?.documentos_empresas) || false,
                10: (permissions?.informes && permissions?.reports_empresas) || false,
                29: (permissions?.informes && permissions?.reports_empresas) || false,
                102: (permissions?.analyticsPro && lookerAnalyticsViewer) || false,
            };
        },
        [lookerAnalyticsViewer, standardFieldsConfiguration],
    );

    const onDelete = useCallback(() => {
        successToast({
            text: getLiteral('succes_entitydeletedsuccessfully'),
        });
    }, []);

    const onErrorDelete = useCallback(() => {
        errorToast({
            text: getLiteral('label_failed_delete_opportunity_salesforce'),
        });
    }, []);

    const onFollow = useCallback((bool) => {
        successToast({
            text: bool ? getLiteral('succes_following') : getLiteral('succes_unfollowing'),
        });
    }, []);

    const onErrorFollow = useCallback(() => {
        errorToast({
            text: getLiteral('error_following_account'),
        });
    }, []);

    const getInfo = useCallback(
        (data) => {
            if (!data?.warningText) return null;
            if (!permission.warnings) return null;
            if (!permission.editWarnings && !permission.viewWarningsInEmpresas) return null;
            return data.warningText;
        },
        [permission.editWarnings, permission.viewWarningsInEmpresas, permission.warnings],
    );

    const getOptions = useCallback(
        (data) => {
            if (!data || !Object.keys(data)?.length) return [];
            return getReadOnlyOptions({
                options,
                data,
                crudPermissions,
                isPublicCompany: true,
            });
        },
        [options, crudPermissions],
    );

    return {
        className: 'fm-companies-detail',
        entity: COMPANIES,
        getWidgetsProps,
        getInitialStateWidgets,
        getWidgets,
        getWidgetsPermissions,
        getOptions,
        optionsParams,
        onDelete,
        onErrorDelete,
        onFollow,
        onErrorFollow,
        getInfo,
    };
};

export default useCompaniesDetail;
